/**
 * Dashboard Page
 *
 * Main view showing all intake requests in a DataTable
 * Matches Podiq design with search, filters, and actions
 */

import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../services/api';
import {
  ROUTES,
} from '../config/constants';
import { TYPE_CODES, TYPE_DISPLAY_NAMES, TYPE_VERIFIED_DISPLAY_OVERRIDE } from '../config/requestTypeConstants';
import RequestsTable from '../components/Dashboard/RequestsTable';
import DownloadCSVModal from '../components/DownloadCSVModal';
import ImportCSVModal from '../components/ImportCSVModal';
import EditProductModal from '../components/Dashboard/EditProductModal';
import './Dashboard.css';

const VIEW_ICON = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
  </svg>
);
const EDIT_ICON = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);
const DELETE_ICON = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

function YourRequestRow({ item, category, currentUser, onView, onEdit, onDelete }) {
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'developer';
  const isDraft = item.status === 'Draft';

  let statusLabel, statusStyle;
  if (item.rejection_reason) {
    statusLabel = 'Declined';
    statusStyle = { background: '#fee2e2', color: '#b91c1c' };
  } else if (item.status === 'Verified') {
    statusLabel = 'Completed';
    statusStyle = { background: '#dcfce7', color: '#15803d' };
  } else if (item.status === 'Needs Input') {
    statusLabel = 'Needs Input';
    statusStyle = { background: '#eff6ff', color: '#1d4ed8' };
  } else if (category === 'approve') {
    statusLabel = 'Awaiting approval';
    statusStyle = { background: '#fff7ed', color: '#c2410c' };
  } else {
    statusLabel = 'Draft';
    statusStyle = { background: '#f3f4f6', color: '#6b7280' };
  }

  const showEdit = isDraft && (category === 'waiting' || category === 'approve' || isAdmin);
  const showDelete = isDraft && (category === 'waiting' || isAdmin);

  return (
    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
      <td style={{ padding: '10px 14px', color: '#374151' }}>{TYPE_DISPLAY_NAMES[item.type_code] || item.type_name}</td>
      <td style={{ padding: '10px 14px', color: '#111827' }}>
        <div style={{ fontWeight: '500' }}>{item.display_name}</div>
      </td>
      <td style={{ padding: '10px 14px', color: '#374151' }}>{item.ski_release_name || '—'}</td>
      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{item.submitter_name}</td>
      <td style={{ padding: '10px 14px' }}>
        <span style={{ fontSize: '12px', fontWeight: '500', padding: '2px 8px', borderRadius: '12px', ...statusStyle }}>
          {statusLabel}
        </span>
      </td>
      <td style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button className="requests-table__action-btn" onClick={() => onView(item.id)} title="View">{VIEW_ICON}</button>
          {showEdit && <button className="requests-table__action-btn" onClick={() => onEdit(item.id)} title="Edit">{EDIT_ICON}</button>}
          {showDelete && <button className="requests-table__action-btn requests-table__action-btn--danger" onClick={() => onDelete(item)} title="Delete">{DELETE_ICON}</button>}
        </div>
      </td>
    </tr>
  );
}

const SESSION_KEY = 'dashboard_view';

const loadSessionState = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)); }
  catch { return null; }
};

const saveSessionState = (patch) => {
  try {
    const current = loadSessionState() || {};
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {}
};

const Dashboard = () => {
  const history = useHistory();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productTotal, setProductTotal] = useState(0);

  const _session = useRef(loadSessionState());
  // true once we've either loaded from sessionStorage or computed the first-visit default
  const viewStateInitialized = useRef(_session.current !== null);

  // Filters — restore search from sessionStorage on mount
  const [filters, setFilters] = useState({
    search: _session.current?.search || '',
    request_type: [],
    status: [],
  });

  // Table view toggles — lifted here so they survive navigation
  const [showAllProducts, setShowAllProductsState] = useState(_session.current?.showAllProducts ?? true);
  const [showMyProducts, setShowMyProductsState] = useState(_session.current?.showMyProducts ?? false);

  const setShowAllProducts = (val) => {
    setShowAllProductsState(val);
    saveSessionState({ showAllProducts: val });
  };

  const setShowMyProducts = (val) => {
    setShowMyProductsState(val);
    saveSessionState({ showMyProducts: val });
  };

  // Expand/collapse state for product groups
  const [expandedProducts, setExpandedProducts] = useState(new Set());

  // Download CSV modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Import CSV modal state
  const [showImportModal, setShowImportModal] = useState(false);

  // Edit product modal state
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [waitingForApproval, setWaitingForApproval] = useState([]);
  const [pleaseApprove, setPleaseApprove] = useState([]);
  const [yourRequestsOpen, setYourRequestsOpen] = useState(_session.current?.yourRequestsOpen ?? true);
  const [yourRequestsDraftOnly, setYourRequestsDraftOnly] = useState(_session.current?.yourRequestsDraftOnly ?? true);
  useEffect(() => { saveSessionState({ yourRequestsOpen }); }, [yourRequestsOpen]);
  useEffect(() => { saveSessionState({ yourRequestsDraftOnly }); }, [yourRequestsDraftOnly]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { item, isApprovalFlow }

  // Group requests by product, and also include products with no requests
  const groupedRequests = React.useMemo(() => {
    const groups = {};

    // Build a lookup of full product data (includes linkedCount) by product id
    const productById = {};
    products.forEach(p => { productById[p.id] = p; });

    requests.forEach(request => {
      const productId = request.product_id || 'uncategorized';

      if (!groups[productId]) {
        // Merge owner + linkedCount from the full products list — the embedded product in
        // request responses only carries owner_id (raw FK), not the joined owner object.
        const baseProduct = request.product || { product_name: 'Uncategorized', categorization: null };
        const fullProduct = productById[productId];
        groups[productId] = {
          productId,
          product: fullProduct ? { ...baseProduct, linkedCount: fullProduct.linkedCount, owner: fullProduct.owner } : baseProduct,
          children: [],
          childCount: 0,
          statuses: {},
        };
      }

      groups[productId].children.push(request);
      groups[productId].childCount++;

      const raw = request.status || '';
      const status = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      groups[productId].statuses[status] = (groups[productId].statuses[status] || 0) + 1;
    });

    // Add all products (including those with no requests) so every product is always visible
    products.forEach(product => {
      if (!groups[product.id]) {
        groups[product.id] = {
          productId: product.id,
          product,
          children: [],
          childCount: 0,
          statuses: {},
        };
      }
    });

    return Object.values(groups).sort((a, b) =>
      (a.product.product_name || '').localeCompare(b.product.product_name || '')
    );
  }, [requests, products]);

  // Fetch requests and products when filters change or when navigating to this page
  // location.key changes every time we navigate, ensuring fresh data when returning from edit
  useEffect(() => {
    fetchRequests();
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, location.key]);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.getCurrentUser();
        setCurrentUser(response.data.data.user || response.data.data);
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  const fetchDashboardRequests = () => {
    api.getMyDashboardRequests()
      .then(res => {
        setWaitingForApproval(res.data.data.waitingForApproval || []);
        setPleaseApprove(res.data.data.pleaseApprove || []);
      })
      .catch(() => {
        setWaitingForApproval([]);
        setPleaseApprove([]);
      });
  };

  useEffect(() => {
    fetchDashboardRequests();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequests = async (bustCache = false) => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 5000,
        ...filters,
      };

      // Add cache-busting parameter when needed
      if (bustCache) {
        params._t = Date.now();
      }

      const response = await api.getRequests(params);
      setRequests(response.data.data.requests);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Compute first-visit default for showMyProducts once data is ready
  useEffect(() => {
    if (viewStateInitialized.current) return;
    if (!currentUser || groupedRequests.length === 0) return;

    const isPrivileged = currentUser.role === 'admin' || currentUser.role === 'developer';
    const ownsAny = !isPrivileged && groupedRequests.some(g => g.product.owner?.id === currentUser.id);
    setShowMyProductsState(ownsAny);
    viewStateInitialized.current = true;
    saveSessionState({ search: filters.search, showAllProducts, showMyProducts: ownsAny });
  }, [currentUser, groupedRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    saveSessionState({ search: searchTerm });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleAddNew = () => {
    history.push(ROUTES.INTAKE_NEW);
  };

  const handleView = (request) => {
    history.push(`/request/${request.id}`);
  };

  const handleEdit = (request) => {
    history.push(`/intake/${request.id}`);
  };

  const handleDelete = async (request) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await api.deleteRequest(request.id);
      fetchRequests();
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Failed to delete request: ' + err.message);
    }
  };

  const APPROVAL_FLOW_TYPES = ['BREAKING_CHANGE', 'CIC', 'PPP', 'PRIVACY', 'SOR', 'PRICING_PACKAGING'];

  const handleDeleteYourRequest = (item) => {
    const isApprovalFlow = APPROVAL_FLOW_TYPES.includes(item.type_code);
    setDeleteConfirm({ item, isApprovalFlow });
  };

  const confirmDeleteYourRequest = async () => {
    if (!deleteConfirm) return;
    try {
      await api.deleteRequest(deleteConfirm.item.id);
      setDeleteConfirm(null);
      fetchDashboardRequests();
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Failed to delete request: ' + err.message);
    }
  };

  const toggleProduct = (productId) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedProducts(new Set(groupedRequests.map(g => g.productId)));
  };

  const collapseAll = () => {
    setExpandedProducts(new Set());
  };

  const handleDownloadCSV = () => {
    setShowDownloadModal(true);
  };

  const fetchUsers = async () => {
    try {
      const response = await api.getAllUsers();
      setUsers(response.data.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.getProducts({ limit: 1000 });
      const prods = response.data.data.products || [];
      setProducts(prods);
      setProductTotal(prods.length);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowEditProductModal(true);
  };

  const handleSaveProduct = async (productId, productData) => {
    try {
      if (productId) {
        // Edit mode - update existing product
        await api.updateProduct(productId, productData);
      } else {
        // Create mode - create new product
        await api.createProduct(productData);
      }
      // Wait for data to refresh before closing modal, bust cache to ensure fresh data
      await fetchRequests(true); // Pass true to bust cache
      setShowEditProductModal(false);
      setEditingProduct(null);
    } catch (err) {
      throw new Error(err.message || 'Failed to save product');
    }
  };

  const handleCloseEditModal = () => {
    setShowEditProductModal(false);
    setEditingProduct(null);
  };

  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleImportCSV = async (file, requestType) => {
    if (!file) return;

    try {
      setLoading(true);
      const response = await api.importCSV(file);

      const { imported, failed, errors } = response.data.data;
      let message = `Successfully imported ${imported} ${requestType} records.`;

      if (failed > 0) {
        message += ` ${failed} records failed.`;
        if (errors && errors.length > 0) {
          message += '\n\nErrors:\n' + errors.map(e => `Row ${e.row}: ${e.error}`).join('\n');
        }
      }

      alert(message);
      fetchRequests(); // Refresh the list
    } catch (err) {
      console.error('Error importing CSV:', err);
      alert('Failed to import CSV: ' + (err.message || 'Unknown error'));
      throw err; // Re-throw so modal can handle it
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Dashboard</h1>
          <p className="dashboard__subtitle">
            Manage and Track the Product Intake Process
          </p>
        </div>
        <div className="dashboard__actions">
          <button
            className="dashboard__button dashboard__button--secondary"
            onClick={handleDownloadCSV}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Download as CSV
          </button>
          {currentUser?.role === 'admin' && (
            <button
              className="dashboard__button dashboard__button--primary"
              onClick={() => history.push(ROUTES.PRODUCTS_NEW)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              New Product
            </button>
          )}
          <button
            className="dashboard__button dashboard__button--primary"
            onClick={handleAddNew}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            New Request
          </button>
        </div>
      </div>

      {error && (
        <div className="dashboard__error">
          <p>Error loading requests: {error}</p>
          <button onClick={fetchRequests}>Retry</button>
        </div>
      )}

      {/* Your Requests — shown when user has waiting or please-approve requests */}
      {(waitingForApproval.length > 0 || pleaseApprove.length > 0) && (() => {
        const visibleWaiting = yourRequestsDraftOnly
          ? waitingForApproval.filter(r => r.status === 'Draft' || r.status === 'Needs Input')
          : waitingForApproval;
        const hasRows = visibleWaiting.length > 0 || pleaseApprove.length > 0;
        return (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <button
                onClick={() => setYourRequestsOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <svg
                  width="16" height="16" viewBox="0 0 20 20" fill="currentColor"
                  style={{ transition: 'transform 0.2s', transform: yourRequestsOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: '#6b7280', flexShrink: 0 }}
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Your Requests</h2>
              </button>
              <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden', fontSize: '12px', fontWeight: '500' }}>
                <button
                  onClick={() => setYourRequestsDraftOnly(true)}
                  style={{ padding: '3px 10px', border: 'none', cursor: 'pointer', background: yourRequestsDraftOnly ? '#1B4A8A' : '#fff', color: yourRequestsDraftOnly ? '#fff' : '#6b7280', transition: 'background 0.15s' }}
                >
                  Draft only
                </button>
                <button
                  onClick={() => setYourRequestsDraftOnly(false)}
                  style={{ padding: '3px 10px', border: 'none', borderLeft: '1px solid #d1d5db', cursor: 'pointer', background: !yourRequestsDraftOnly ? '#1B4A8A' : '#fff', color: !yourRequestsDraftOnly ? '#fff' : '#6b7280', transition: 'background 0.15s' }}
                >
                  All
                </button>
              </div>
            </div>
            {yourRequestsOpen && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                {hasRows ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Type</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Request</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>SKI Release</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Submitted by</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleWaiting.length > 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: '6px 14px', backgroundColor: '#f3f4f6', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Waiting for approval
                          </td>
                        </tr>
                      )}
                      {visibleWaiting.map((item) => (
                        <YourRequestRow
                          key={item.id}
                          item={item}
                          category="waiting"
                          currentUser={currentUser}
                          onView={(id) => history.push(`/request/${id}`)}
                          onEdit={(id) => history.push(`/intake/${id}`)}
                          onDelete={handleDeleteYourRequest}
                        />
                      ))}
                      {pleaseApprove.length > 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: '6px 14px', backgroundColor: '#fff7ed', fontSize: '12px', fontWeight: '600', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Please approve
                          </td>
                        </tr>
                      )}
                      {pleaseApprove.map((item) => (
                        <YourRequestRow
                          key={item.id}
                          item={item}
                          category="approve"
                          currentUser={currentUser}
                          onView={(id) => history.push(`/request/${id}`)}
                          onEdit={(id) => history.push(`/intake/${id}`)}
                          onDelete={handleDeleteYourRequest}
                        />
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '20px 14px', color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
                    No draft requests — switch to <strong>All</strong> to see completed items.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Delete confirmation modal for Your Requests */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '440px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600' }}>Delete Request</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
              Are you sure you want to delete this request?
              {deleteConfirm.isApprovalFlow && (
                <><br /><br />An approval email may have been sent for this request. If the approver clicks the link in that email after deletion, they will see a &ldquo;Request not found&rdquo; message.</>
              )}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteYourRequest}
                style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
              >
                Delete Request
              </button>
            </div>
          </div>
        </div>
      )}

      <RequestsTable
        groupedRequests={groupedRequests}
        expandedProducts={expandedProducts}
        loading={loading}
        productTotal={productTotal}
        filters={filters}
        currentUser={currentUser}
        products={products}
        showAllProducts={showAllProducts}
        onShowAllProductsChange={setShowAllProducts}
        showMyProducts={showMyProducts}
        onShowMyProductsChange={setShowMyProducts}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onEditProduct={handleEditProduct}
        onToggleProduct={toggleProduct}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
      />

      {/* Download CSV Modal */}
      {showDownloadModal && (
        <DownloadCSVModal onClose={() => setShowDownloadModal(false)} />
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <ImportCSVModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImportCSV}
        />
      )}

      {/* Edit/Create Product Modal */}
      {showEditProductModal && (
        <EditProductModal
          product={editingProduct}
          users={users}
          onClose={handleCloseEditModal}
          onSave={handleSaveProduct}
        />
      )}
    </div>
  );
};

export default Dashboard;
